import { createClient } from '@sanity/client'

const client = createClient({
  projectId: process.env.REACT_APP_SANITY_PROJECT_ID,
  dataset: process.env.REACT_APP_SANITY_DATASET,
  useCdn: false,
  apiVersion: '2024-04-10',
})

export const sanityServices = {
  getAllArticles: async () => {
    try {
      const groqQuery = `*[_type == "article" && isPublished == true] {
        title,
        "slug": slug.current,
        createdAt,
        content
      }`

      const results = await client.fetch(groqQuery)
      return results
    } catch (error) {
      console.error('Error fetching all articles:', error)
      throw new Error('Failed to fetch all articles')
    }
  },
  getFeaturedArticles: async () => {
    try {
      const groqQuery = `*[_type == "article" && isPublished == true && "featured" in tags] {
        title,
        "slug": slug.current,
        createdAt,
        content,
        shortSummary
      }`

      const results = await client.fetch(groqQuery)
      return results
    } catch (error) {
      console.error('Error fetching featured articles:', error)
      throw new Error('Failed to fetch featured articles')
    }
  },
  searchArticles: async (query: string) => {
    try {
      const groqQuery = `*[_type == "article" && isPublished == true && 
        (title match $query || content[].children[].text match $query)
        ] {
        title,
        "slug": slug.current,
        content,
        shortSummary
      }`

      const params = { query: `${query}*` }

      const results = await client.fetch(groqQuery, params)
      return results
    } catch (error: any) {
      console.error('Error fetching search results:', error)
      throw new Error('Failed to fetch search results')
    }
  },
  getArticleById: async (id: string) => {
    try {
      const groqQuery = `*[_type == "article" && isPublished == true && 
        slug.current == $slug
        ] {
        title,
        content,
        createdAt
      }`

      const params = { slug: `${id}` }

      const results = await client.fetch(groqQuery, params)
      return results
    } catch (error: any) {
      console.error('Error fetching search results:', error)
      throw new Error('Failed to fetch search results')
    }
  },
  getAboutUsPage: async () => {
    try {
      const groqQuery = `*[_type == "about-us"] {
        introText,
        "heroImageURL": heroImage.asset->url,
        story,
        coreValues,
        directors{
          title,
          stakeholderList[]{
            "image": image.asset->url,
            name,
            designation,
            about
          },
        },
        
        mission,
        vision
      }`

      const results = await client.fetch(groqQuery)
      return results
    } catch (error) {
      console.error('Error fetching about us page content:', error)
      throw new Error('Failed to fetch about us page content')
    }
  },
  getOpportunityDetailsPageBySlug: async (slug: string) => {
    try {
      const groqQuery = `*[_type == "opportunityDetail" && slug.current == $slug] {
        "logo": logo.asset->url,
        "heroImage": heroImage.asset->url,
        opportunityTitle,
        opportunityColor,
        showInvestorBacking,
        showRaiseGoal,
        opportunityBacking,
        aboutLinks[]{
          linkTitle,
          linkUrl
        },
        estimatedFinancials {
          showSection,
          displayOrder,
          heading,
          disclaimer,
          financialMetrics[] {
            metricName,
            graphType,
            currencyUnits,
            graphColor,
            dataPoints[] {
              year,
              value
            }
          }
        },
        fees {
          targetReturn,
          hurdleRate,
          carryRate,
          upfrontCommission,
          managementFee,
          customKeysValues[]{
            label,
            value
          }
        },
        news {
          showSection,
          displayOrder,
          heading,
          articles[] {
            headline,
            publishedDate,
            summary,
            articleUrl,
            "image": image.asset->url,
            sources[] {
              sourceName,
              sourceUrl
            }
          }
        },
        leadershipTeam {
          showSection,
          displayOrder,
          header,
          backgroundColor,
          sectionTitle,
          sectionPosition,
          text,
          "teamMembers": teamMembers[] {
            "image": image.asset->url,
            name,
            designation,
            about,
            linkedInUrl
          },
        },
        about {
          showSection,
          expandSection,
          displayOrder,
          header,
          backgroundColor,
          aboutText,
          layoutColumns,
          "opportunityImages": opportunityImages[].asset->url
        },
        sourceCountries {
          showSection,
          expandSection,
          displayOrder,
          backgroundColor,
          header,
          sourceCountryText,
          sourceCountryImagePosition,
          "sourceCountryImage": sourceCountryImage.asset->url,
        },
        riskManagement {
          showSection,
          expandSection,
          displayOrder,
          header,
          backgroundColor,
          layoutColumns,
          textItems,
          "riskManagementImage": riskManagementImage.asset->url
        },
        aboutFund {
          showSection,
          expandSection,
          displayOrder,
          header,
          backgroundColor,
          aboutFundText
        },
        regulation {
          showSection,
          expandSection,
          displayOrder,
          header,
          backgroundColor,
          regulationText
        },
        factSheet {
          showSection,
          expandSection,
          displayOrder,
          header,
          backgroundColor,
          "facts": facts[] {
            factTextPrimary,
            factTextSecondary,
            "factTextImage": factTextImage.asset->url
          },
          "images": images[].asset->url,
          sectionText
        },
        process {
          showSection,
          expandSection,
          displayOrder,
          header,
          backgroundColor,
          points
        },
        opportunityImages {
          showSection,
          expandSection,
          displayOrder,
          header,
          backgroundColor,
          "images": images[].asset->url
        },
        opportunityVideos {
          showSection,
          expandSection,
          displayOrder,
          header,
          backgroundColor,
          "videos": videos[].asset->url
        },
        executiveSummary {
          showSection,
          expandSection,
          displayOrder,
          header,
          backgroundColor,
          executiveSummaryText,
          "executiveSummaryImages": images[].asset->url,
          layoutColumns
        },
        strengthSections[] {
          _key,
          showSection,
          expandSection,
          displayOrder,
          header,
          backgroundColor,
          strengthItems,
          layoutColumns,
          imageLayoutColumns,
          "images": images[].asset->url,
          text,
          sectionVariant,
          "sectionsWithCards": sectionsWithCards[] {
            sectionTitle,
            sectionVariant,
            layoutColumns,
            strengthItems,
            "image": image.asset->url,
            imagePosition,
            text
          }
        },
        strengths {
          showSection,
          expandSection,
          displayOrder,
          header,
          backgroundColor,
          strengthItems,
          layoutColumns,
          imageLayoutColumns,
          "images": images[].asset->url,
          text,
          sectionVariant,
          "sectionsWithCards": sectionsWithCards[] {
            sectionTitle,
            sectionVariant,
            layoutColumns,
            strengthItems,
            "image": image.asset->url,
            imagePosition,
            text
          }
        },
        competitiveAdvantage {
          showSection,
          expandSection,
          displayOrder,
          header,
          backgroundColor,
          text,
          imagePosition,
          "image": image.asset->url
        },
        whyShouldYouInvest {
          showSection,
          expandSection,
          displayOrder,
          header,
          backgroundColor,
          "reasons": reasons[] {
            primaryText,
            blockText,
            "image": image.asset->url,
            imagePosition
          }
        },
        keyPartnerships {
          showSection,
          expandSection,
          displayOrder,
          header,
          backgroundColor,
          layoutColumns,
          sectionVariant,
          "partnerShips": partnerShips[] {
            "partnerImage": partnerImage.asset->url,
            text
          },
          partnerShipText
        },
        clientBase {
          showSection,
          expandSection,
          displayOrder,
          header,
          backgroundColor,
          sectionTitle,
          "clients": clients[].asset->url,
        },
        risksInvolved {
          showSection,
          expandSection,
          displayOrder,
          header,
          backgroundColor,
          text
        },
        additionalSections {
          showSection,
          expandSection,
          displayOrder,
          header,
          backgroundColor,
          "sections": sections[] {
              showSection,
              expandSection,
              sectionTitle,
              backgroundColor,
              "image": image.asset->url,
              imagePosition,
              sectionText,
              sectionVariant,
              "images": images[].asset->url,
          }
        }
    }`

      const params = { slug: `${slug}` }

      const results = await client.fetch(groqQuery, params)
      return results
    } catch (error: any) {
      console.error('Error fetching opportunity details results:', error)
      throw new Error('Failed to fetch opportunity details results')
    }
  },
}
